import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import Toast from '../components/Toast';
import api from '../api';

/**
 * /admin/waitlist — Phase 7 · Change 8
 *
 * Lists everyone who joined via the "no invite code" waitlist modal and lets
 * admins generate a single-use, platform-issued invite code they can share
 * with the person (via WhatsApp/email/DMs manually).
 *
 * Code generation is idempotent — if a code was already issued, we display
 * that same code instead of creating a new one.
 */
export default function AdminWaitlist() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, invited: 0, pending: 0 });
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // email currently being invited
  const [toast, setToast] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/waitlist', {
        params: { status: tab, search: search || undefined, limit: 500 },
      });
      setRows(data.waitlist || []);
      setCounts({
        total: data.total || 0,
        invited: data.total_invited || 0,
        pending: data.total_pending || 0,
      });
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Failed to load waitlist.' });
    } finally { setLoading(false); }
  }, [tab, search]);

  useEffect(() => { refresh(); }, [refresh]);

  const generate = async (email) => {
    setBusy(email);
    try {
      const { data } = await api.post(`/admin/waitlist/${encodeURIComponent(email)}/invite`);
      setToast({
        msg: data.already_existed
          ? `Reusing existing invite code: ${data.code}`
          : `✓ Generated code ${data.code} for ${email}`,
      });
      // Optimistically copy to clipboard
      try { await navigator.clipboard.writeText(data.code); } catch {}
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Failed to generate invite.' });
    } finally { setBusy(null); }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setToast({ msg: `✓ Copied ${code}` });
    } catch {}
  };

  const exportCsv = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const win = window.open(`${API}/api/admin/waitlist/export/csv`, '_blank');
    if (!win) setToast({ msg: 'Pop-ups blocked — allow pop-ups to download.' });
  };

  return (
    <AdminLayout active="waitlist">
      <div className="admin-inner" data-testid="admin-waitlist">
        <div className="admin-hdr">
          <h1 className="admin-title">Waitlist</h1>
          <div className="admin-sub">
            {counts.total.toLocaleString()} people · {counts.invited} invited · {counts.pending} pending
          </div>
        </div>

        <div className="admin-waitlist-tabs">
          {[
            { key: 'all', label: `All · ${counts.total}` },
            { key: 'pending', label: `Pending · ${counts.pending}` },
            { key: 'invited', label: `Invited · ${counts.invited}` },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              className={`admin-waitlist-tab${tab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}
              data-testid={`waitlist-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <input
            type="text"
            className="text-input"
            placeholder="Search email or socials…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 260 }}
            data-testid="waitlist-search"
          />
          <button
            type="button"
            className="admin-invite-btn"
            style={{ background: 'rgba(6, 8, 14, 0.75)' }}
            onClick={exportCsv}
            data-testid="waitlist-export-csv"
          >
            Export CSV
          </button>
        </div>

        {loading && <div className="loading-shell" data-testid="waitlist-loading">Loading waitlist…</div>}

        {!loading && rows.length === 0 && (
          <div className="admin-empty" data-testid="waitlist-empty">
            No entries match this filter yet.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <table className="admin-waitlist-table" data-testid="waitlist-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Socials</th>
                <th>Joined</th>
                <th style={{ textAlign: 'right' }}>Invite</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const joined = r.created_at ? new Date(r.created_at).toLocaleDateString() : '—';
                return (
                  <tr key={r.id} data-testid={`waitlist-row-${r.email}`}>
                    <td><b>{r.email}</b></td>
                    <td style={{ color: '#6b6580' }}>{r.socials || '—'}</td>
                    <td style={{ color: '#6b6580', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{joined}</td>
                    <td style={{ textAlign: 'right' }}>
                      {r.invite_code ? (
                        <>
                          <span
                            className="admin-invite-code"
                            onClick={() => copyCode(r.invite_code)}
                            role="button"
                            title="Click to copy"
                            data-testid={`waitlist-code-${r.email}`}
                          >
                            {r.invite_code}
                          </span>
                          <button
                            type="button"
                            className="admin-invite-btn"
                            style={{ background: 'rgba(0, 180, 255, 0.75)' }}
                            onClick={() => copyCode(r.invite_code)}
                            data-testid={`waitlist-copy-${r.email}`}
                          >
                            Copy
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="admin-invite-btn"
                          disabled={busy === r.email}
                          onClick={() => generate(r.email)}
                          data-testid={`waitlist-invite-${r.email}`}
                        >
                          {busy === r.email ? 'Generating…' : 'Generate invite'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AdminLayout>
  );
}
